import type {WorkstreamDraftPolicy, WorkstreamDraftPolicyUpdate} from 'client';
import {getBackendUrl} from 'utilities';

export const workstreamDraftPolicyQueryKey = ['workstream', 'draft-policy'] as const;

export async function fetchWorkstreamDraftPolicy(): Promise<WorkstreamDraftPolicy> {
  const response = await fetch(`${getBackendUrl()}/api/v1/workstream/policy`, {
    credentials: 'include',
    headers: {Accept: 'application/json'},
  });
  if (!response.ok) {
    throw new Error(`Failed to load workstream policy (${response.status})`);
  }
  return (await response.json()) as WorkstreamDraftPolicy;
}

export async function putWorkstreamDraftPolicy(
  body: WorkstreamDraftPolicyUpdate
): Promise<WorkstreamDraftPolicy> {
  const response = await fetch(`${getBackendUrl()}/api/v1/workstream/policy`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      start_date: body.start_date ?? null,
      end_date: body.end_date ?? null,
    }),
  });
  if (!response.ok) {
    throw new Error(`Failed to save workstream policy (${response.status})`);
  }
  return (await response.json()) as WorkstreamDraftPolicy;
}

export function isMetricDraftReasonWaived(
  policy: WorkstreamDraftPolicy | undefined,
  metricCreatedAt: string | null | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!isWorkstreamPlanningWindowActive(policy, nowMs)) {
    return false;
  }
  if (!metricCreatedAt) {
    return false;
  }
  const created = Date.parse(metricCreatedAt);
  if (!Number.isFinite(created)) {
    return false;
  }
  const start = policy?.start_date ? Date.parse(policy.start_date) : null;
  if (start != null && Number.isFinite(start) && created < start) {
    return false;
  }
  const end = Date.parse(policy?.end_date ?? '');
  if (created > end) {
    return false;
  }
  return true;
}

export function isWorkstreamPlanningWindowActive(
  policy: WorkstreamDraftPolicy | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!policy?.end_date) {
    return false;
  }
  const end = Date.parse(policy.end_date);
  if (!Number.isFinite(end) || nowMs > end) {
    return false;
  }
  const start = policy.start_date ? Date.parse(policy.start_date) : null;
  if (start != null && Number.isFinite(start) && nowMs < start) {
    return false;
  }
  return true;
}

export const utcDayStartIso = (d: Date) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)).toISOString();

export const utcDayEndIso = (d: Date) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)).toISOString();
